import "@hotwired/turbo-rails"
import React from "react"
import { createRoot } from "react-dom/client"
import SpinWheelApp from "./components/nyp/SpinWheelApp"
import * as RouteOptimizer from "./components/rodg/helpers/routeOptimizer"

window.RouteOptimizer = RouteOptimizer

const roots = new Map() // container element -> React root

const mountComponent = (el, Component) => {
  if (roots.has(el)) return
  const root = createRoot(el)
  root.render(<Component />)
  roots.set(el, root)
}

const mountDynamic = (id, importFn) => {
  const el = document.getElementById(id)
  if (!el || roots.has(el)) return
  importFn().then(({ default: Component }) => {
    if (roots.has(el)) return // re-check after async import
    mountComponent(el, Component)
  })
}

const unmountOrphaned = () => {
  for (const [el, root] of roots) {
    if (!document.contains(el)) {
      root.unmount()
      roots.delete(el)
    }
  }
}

// Full page transitions: unmount non-persistent roots, preserve the rest
const persistentRoots = new Map() // id -> element (saved for re-insertion after render)

document.addEventListener("turbo:before-render", (event) => {
  const newBody = event.detail.newBody
  persistentRoots.clear()

  for (const [el, root] of roots) {
    if (el.id && newBody.querySelector(`#${el.id}`)) {
      persistentRoots.set(el.id, el) // keep alive across body swap
    } else {
      root.unmount()
      roots.delete(el)
    }
  }
})

// After body swap: re-insert preserved React trees into the new document
document.addEventListener("turbo:render", () => {
  for (const [id, oldEl] of persistentRoots) {
    const placeholder = document.getElementById(id)
    if (placeholder && placeholder !== oldEl) {
      placeholder.replaceWith(oldEl) // swap empty placeholder for live React tree
    }
  }
  persistentRoots.clear()
})

// Page cache: unmount only frame roots
document.addEventListener("turbo:before-cache", () => {
  for (const [el, root] of roots) {
    if (el.closest("turbo-frame")) {
      root.unmount()
      roots.delete(el)
    }
  }
})

// Before RODG frame content swap: cleanup inline scripts and lock height
document.addEventListener("turbo:before-frame-render", (event) => {
  if (event.target.id !== "rodg-page") return
  document.dispatchEvent(new Event("rodg-page-cleanup"))
  event.target.style.minHeight = event.target.offsetHeight + "px"
})

// After RODG frame renders: unmount orphaned roots and release height
document.addEventListener("turbo:frame-render", (event) => {
  if (event.target.id !== "rodg-page") return
  unmountOrphaned()
  requestAnimationFrame(() => {
    event.target.style.minHeight = ""
  })
})

const mountRodgPageComponents = () => {
  mountDynamic("rodg-wheel-root", () => import("./components/rodg/Wheel"))
  mountDynamic("rodg-map-root", () => import("./components/rodg/Map"))
}

// Mount RODG frame components after frame content loads
document.addEventListener("turbo:frame-load", (event) => {
  if (event.target.id !== "rodg-page") return
  mountRodgPageComponents()
})

// Mount all components on full page load
document.addEventListener("turbo:load", () => {
  const container = document.getElementById("spin-wheel-root")
  if (container) {
    mountComponent(container, SpinWheelApp)
  }

  mountDynamic("rodg-auth-root", () => import("./components/rodg/Auth"))
  mountRodgPageComponents()
})
